import axios from "axios";
import { prepareContentItemsForRenderCustom } from "../../custom/render/renderItemCustom.tsx";
import type { PageData } from "../data/pageData.ts";
import { ItemTypeBase } from "../enums.ts";
import { getAllEnv, getEnv } from "../helpers/env.ts";
import { objectClone } from "../helpers/utils.ts";
import { fetchPageById, fetchPageByUrl } from "../loaders.ts";
import type { PageItemData, PageRenderedItemData, PageRenderedSectionData, PageSectionData } from "../page/content.types.ts";
import type { LexicalEditor } from "lexical";
import { format } from "date-fns";

export interface RenderStateHelper {
	editor: LexicalEditor,
	editorCode: LexicalEditor,
	renderEditorState: Function,
}

export interface RenderOptions {
	skipReusable?: Array<number>,
	skipState?: boolean,
	renderFlat?: boolean,
	skipData?: boolean,
	tags?: object,
}

export async function preparePageForRender(page: PageData, renderStateHelper: RenderStateHelper, options: RenderOptions = {}): Promise<PageData> {
	if (page) {
		options = {
			...options,
			tags: {
				...getAllEnv(),
				...options?.tags,
				PAGE_DATE: format(page.obj.creationDate, "do MMMM yyyy"),
				PAGE_TIME: format(page.obj.creationDate, "hh:mm")
			}
		};

		if (options.hasOwnProperty('skipReusable') === false) {
			options.skipReusable = [];
		}
		if (options.skipReusable.includes(page.id) === false) {
			options.skipReusable.push(page.id);
		}

		return {
			...page,
			content: await prepareContentDataForRender(page?.content, renderStateHelper, objectClone(options)),
			cover: page?.coverId ? await getRepoObjectWithId('asset', page.coverId) : undefined,
			header: page?.headerId ? await getRepoObjectWithId('asset', page.headerId) : undefined,
		};
	}

	return Promise.resolve(null);
}

export async function fetchPageRenderReadyByUrl(url: string, renderStateHelper: RenderStateHelper, options: RenderOptions = {}) {
	return fetchPageByUrl(url).then((page) => {
		return preparePageForRender(page, renderStateHelper, options);
	});
}

export async function fetchPageRenderReadyById(id: number, renderStateHelper: RenderStateHelper, options: RenderOptions = {}) {
	return fetchPageById(id).then(async (page) => {
		return preparePageForRender(page, renderStateHelper, options);
	});
}

let repoCache = {};

function repoAddObject(objectClass: string, id: number, object: any) {
	repoCache = {
		...repoCache,
		[objectClass]: {
			...repoCache[objectClass],
			[id]: object
		}
	};
}

export function getRepoObjectWithId(objectClass: string, id: number, renderStateHelper?: RenderStateHelper, options: RenderOptions = {}) {
	if (id) {
		if (repoCache?.hasOwnProperty(objectClass) && repoCache[objectClass]?.hasOwnProperty(id) && repoCache[objectClass][id] !== null) {
			return Promise.resolve(repoCache[objectClass][id]);
		} else {
			try {
				if (objectClass === 'page') {
					return fetchPageRenderReadyById(id, renderStateHelper, options).then((response) => {
						repoAddObject(objectClass, id, response);
						return response;
					});
				} else {
					return axios(getEnv('PUBLIC_API_HOST') + '/api/' + objectClass + '/' + id)
						.then(function (response) {
							repoAddObject(objectClass, id, response.data);
							return response.data;
						}).catch(function (error) {
							if (error.response && error.config) {
								console.log('Axios ' + error.response.status + ':', error.config.url)
							}
						});
				}
			} catch (error) {
				console.log(error);
			}
		}
	}

	console.info('Repo: no id given');
	return null;
}

/**
 * this function prepares the content tree for immediate consumption by renderers
 *
 * 	1. fill content tree with missing any reused content.
 * 	2. fill referenced data objects (assets, images etc.)
 *  3. render all the editor states present in the content tree to html
 */
export function prepareContentDataForRender(content: PageSectionData[], renderStateHelper: RenderStateHelper, options: RenderOptions) {
	if (content && Array.isArray(content)) {
		const promises = content.map(async (section: PageRenderedSectionData) => {

			if (section.reusable && !options.renderFlat) {
				//console.log(section.reusable, options);
				if (options.hasOwnProperty('skipReusable') === false) {
					options.skipReusable = [];
				}

				if (options?.skipReusable?.includes(section.reusable) === false) {
					//console.log("loading reusable section: " + section.reusable);
					section.page = await getRepoObjectWithId('page', section.reusable, renderStateHelper, objectClone(options)).then((data) => {
						options.skipReusable.push(section.reusable);
						return data;
					})
				} else {
					console.log('section skipping loading page: ' + section.reusable);
				}
			}

			if (!options?.skipData) {
				if (section.backgroundImageId) {
					section.backgroundImage = await getRepoObjectWithId('asset', section.backgroundImageId);
				}
			}

			if (section.items) {
				section.items = await prepareContentItemsForRender(section.items, renderStateHelper, options);
			}

			return section;
		});

		return Promise.all(promises);
	}

	return Promise.resolve(null);
}

export const renderTags = (input: string = "", tags: object = {}) => {
	for (const key in tags) {
		input = input.replaceAll('{{' + key + '}}', tags[key]);
	}

	return input;
}

export function prepareContentItemsForRender(items: PageItemData[], renderStateHelper: RenderStateHelper, options: RenderOptions = {}) {
	if (items && Array.isArray(items)) {
		const promises = items.map(async (item: PageRenderedItemData) => {
			if (!options?.skipData && item.backgroundImageId) {
				item.backgroundImage = await getRepoObjectWithId('asset', item.backgroundImageId);
			}

			switch (item.type) {
				case ItemTypeBase.REUSE:
				case ItemTypeBase.SLIDER:
				case ItemTypeBase.TABS:
					if (!options?.renderFlat && item.content) {
						if (options.hasOwnProperty('skipReusable') === false) {
							options.skipReusable = [];
						}

						if (options?.skipReusable?.includes(parseInt(item.content)) === false) {
							//console.log("loading item reusable: " + item.content, options);
							item.page = await getRepoObjectWithId('page', parseInt(item.content), renderStateHelper, {...objectClone(options)})
								.then((data) => {
									options.skipReusable.push(parseInt(item.content));
									return data;
								});
						} else {
							console.log('item skipping loading page: ' + item.content);
						}
					}
					break;

				case ItemTypeBase.CODE:
					if (!options?.skipState && item.editorState) {
						try {
							const state = renderStateHelper.editorCode.parseEditorState(item.editorState);
							item.rendered = renderStateHelper.renderEditorState(renderStateHelper.editorCode, state);
							item.rendered = renderTags(item.rendered, {
								...getAllEnv(),
								...options.tags
							});
						} catch {
							console.info('wrong text item code state:');
							console.dir(item.editorState, {depth: 3});
						}
					}
					break;

				case ItemTypeBase.TEXT:
				case ItemTypeBase.TEXT_ANCHOR:
				case ItemTypeBase.FAQ:
					if (!options?.skipState && item.editorState) {
						try {
							const state = renderStateHelper.editor.parseEditorState(item.editorState);
							item.rendered = renderStateHelper.renderEditorState(renderStateHelper.editor, state);
							item.rendered = renderTags(item.rendered, {
								...getAllEnv(),
								...options.tags
							});
						} catch {
							console.info('wrong text item state:');
							console.dir(item.editorState, {depth: 3});
						}
					}
					break;

				case ItemTypeBase.CONTAINER:
					if (Array.isArray(item.content)) {
						item.content = await prepareContentItemsForRender(item.content, renderStateHelper, options);
					}
					break;
			}

			item = await prepareContentItemsForRenderCustom(item, renderStateHelper, options);

			return item;
		});

		return Promise.all(promises);
	}

	return Promise.resolve(null);
}

export function cleanupContentData(content: PageRenderedSectionData[]) {
	if (content && Array.isArray(content)) {
		return content.map((section) => {
			delete section.page;
			delete section.backgroundImage;

			if (section.items && Array.isArray(section.items) ) {
				section.items = section.items.map((item: PageRenderedItemData) => {
					delete item.backgroundImage;
					delete item.page;
					delete item.latestNews;

					// @ts-ignore
					delete item.pager;
					// @ts-ignore
					delete item.navigation;
					// @ts-ignore
					delete item.delay;
					// @ts-ignore
					delete item.autoplay;
					// @ts-ignore
					delete item.loop;

					return item;
				});
			}

			return section;
		});
	}

	return null;
}
